import { useCallback, useEffect, useState } from "react";

import { useAddressFormBlockContext } from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.context";
import { BPostAddressField } from "@/components/Form/RHF/FormFields/BPostAddressField/bPostAddressField.container";
import { CountryField } from "@/components/Form/RHF/FormFields/CountryField/countryField.container";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { ZipCodeField } from "@/components/Form/RHF/FormFields/ZipCodeField/zipCodeField.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { MessageComponent } from "@/components/General/Messages/messages.component";
import { COUNTRY_BE } from "@/components/WithdrawalForm/helpers/address.helpers";
import { WithdrawalFormValues } from "@/components/WithdrawalForm/withdrawalFrom.schema";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Props = {
    disabled?: boolean;
    country: string;
};

export const AddressPartial = ({ disabled: propsDisabled, country }: Props) => {
    const { t } = useGranite();
    const {
        displayAddressFields,
        setDisplayAddressFields,
        disabled: addressFormBlockDisabled
    } = useAddressFormBlockContext();

    const [displayTimeout, setDisplayTimeout] = useState(false);

    const handleOnManualEntry = useCallback(() => {
        setDisplayAddressFields(true);
    }, [setDisplayAddressFields]);

    const handleOnTimeout = useCallback(() => setDisplayTimeout(true), []);

    const disabled = propsDisabled || addressFormBlockDisabled;

    useEffect(() => {
        if (disabled) return;
        if (displayAddressFields) return;
        if (country === COUNTRY_BE) return;

        handleOnManualEntry();
    }, [disabled, displayAddressFields, handleOnManualEntry, country]);

    return (
        <>
            {!displayAddressFields && (
                <Row>
                    <Col md={12}>
                        {displayTimeout && (
                            <MessageComponent
                                message={{
                                    text: t("bpost.timeout"),
                                    id: "bpost-timeout",
                                    status: "warning",
                                    dismissible: false,
                                    postFix: {
                                        text: t("bpost.timeout.manuelEntry"),
                                        onClick: () => {
                                            setDisplayAddressFields(true);
                                        }
                                    }
                                }}
                            />
                        )}
                        <BPostAddressField<WithdrawalFormValues>
                            test={`customerAddress-input-address`}
                            name={"address.address"}
                            label={t("general.form.address.addressLine.label")}
                            placeholder={t(
                                "general.form.address.addressLine.placeholder"
                            )}
                            required={false}
                            onManualEntry={handleOnManualEntry}
                            onTimeout={handleOnTimeout}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            )}
            {displayAddressFields && (
                <>
                    <Row>
                        <Col md={12}>
                            <CountryField<WithdrawalFormValues>
                                label={t("general.form.address.country.label")}
                                name={"address.country"}
                                required={true}
                                onChange={() => {}}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <TextField<WithdrawalFormValues>
                                name={"address.street"}
                                label={t("general.form.address.street.label")}
                                required={false}
                                disabled={disabled}
                                placeholder={t(
                                    "general.form.address.street.placeholder"
                                )}
                                autoComplete={"street-address"}
                            />
                        </Col>
                        <Col md={3}>
                            <TextField<WithdrawalFormValues>
                                name={"address.number"}
                                label={t("general.form.address.number.label")}
                                required={false}
                                disabled={disabled}
                                placeholder={t(
                                    "general.form.address.number.placeholder"
                                )}
                                autoComplete={"off"}
                            />
                        </Col>
                        <Col md={3}>
                            <TextField<WithdrawalFormValues>
                                name={"address.box"}
                                disabled={disabled}
                                required={false}
                                label={t("general.form.address.box.label")}
                                placeholder={t(
                                    "general.form.address.box.placeholder"
                                )}
                                autoComplete={"off"}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ZipCodeField<WithdrawalFormValues>
                                name={"address.zipCode"}
                                label={t("general.form.address.zipCode.label")}
                                disabled={disabled}
                                required={false}
                                autoComplete={"postal-code"}
                                countryPath={"address.country"}
                            />
                        </Col>
                        <Col md={6}>
                            <TextField<WithdrawalFormValues>
                                name={"address.city"}
                                label={t("general.form.address.city.label")}
                                disabled={disabled}
                                required={false}
                                placeholder={t(
                                    "general.form.address.city.placeholder"
                                )}
                                autoComplete={"address-level2"}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};
